import type { UseQueryResult } from '@tanstack/react-query';
import type { SearchResult } from '@/services/auth/types';

export const SOURCE_KEYS = [
  'search',
  'people',
  'docs',
  'navigation',
  'integrations',
  'adAudiences',
  'forms',
  'webhooks',
  'cdpOut',
  'dataOut',
  'objectTypes',
] as const;

export type SourceKey = (typeof SOURCE_KEYS)[number];

/** Which data sources are active for the current search session. */
export type ActiveSources = Record<SourceKey, boolean>;

export const NO_SOURCES = Object.fromEntries(SOURCE_KEYS.map((key) => [key, false])) as ActiveSources;

const EAGER_WORKSPACE_SOURCE_KEYS = ['search', 'people', 'navigation'] as const satisfies ReadonlyArray<SourceKey>;

const DEFERRED_WORKSPACE_SOURCE_KEYS = [
  'integrations',
  'adAudiences',
  'forms',
  'webhooks',
  'cdpOut',
  'dataOut',
  'objectTypes',
] as const satisfies ReadonlyArray<SourceKey>;

// Sources that fire only after the user scrolls to the sentinel in 'all' mode.
// In their dedicated filter modes (activeCategory === key) they fire immediately.
export const DEFERRED_SOURCE_KEYS = new Set<SourceKey>(['docs', ...DEFERRED_WORKSPACE_SOURCE_KEYS]);

export const ORDERED_SOURCE_KEYS = [
  'docs',
  'navigation',
  'integrations',
  'adAudiences',
  'forms',
  'webhooks',
  'cdpOut',
  'dataOut',
  'objectTypes',
] as const satisfies ReadonlyArray<Exclude<SourceKey, 'search' | 'people'>>;

export type SourceQueryState = Pick<UseQueryResult<unknown>, 'isError' | 'isLoading' | 'isFetching' | 'dataUpdatedAt'>;

export type SearchSource<TResult extends SearchResult = SearchResult> = SourceQueryState & {
  results: TResult[];
};

type BuildSourceEnabledStatesOptions = {
  activeSources: ActiveSources;
  activeCategory: string;
  isSearchingEnabled: boolean;
  isDocsSearchingEnabled: boolean;
  deferredEnabled: boolean;
};

export function hasAnyDeferredSource(activeSources: ActiveSources): boolean {
  return [...DEFERRED_SOURCE_KEYS].some((key) => activeSources[key]);
}

export function buildSourceEnabledStates({
  activeSources,
  activeCategory,
  isSearchingEnabled,
  isDocsSearchingEnabled,
  deferredEnabled,
}: BuildSourceEnabledStatesOptions): ActiveSources {
  const deferredSourcesEnabled = activeCategory !== 'all' || deferredEnabled;
  const sourceEnabled = { ...NO_SOURCES };

  for (const key of EAGER_WORKSPACE_SOURCE_KEYS) {
    sourceEnabled[key] = activeSources[key] && isSearchingEnabled;
  }

  sourceEnabled.docs = activeSources.docs && isDocsSearchingEnabled && deferredSourcesEnabled;

  for (const key of DEFERRED_WORKSPACE_SOURCE_KEYS) {
    sourceEnabled[key] = activeSources[key] && isSearchingEnabled && deferredSourcesEnabled;
  }

  return sourceEnabled;
}

export function mergeQueryStates(states: readonly SourceQueryState[]): SourceQueryState {
  return {
    isError: states.some((state) => state.isError),
    isLoading: states.some((state) => state.isLoading),
    isFetching: states.some((state) => state.isFetching),
    dataUpdatedAt: Math.max(0, ...states.map((state) => state.dataUpdatedAt)),
  };
}
